import PropTypes from 'prop-types';
import React from 'react';
import ContactUsStyles from './ContactUs.styled';
import Layout from '../../components/Layout/Layout';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import theme from '../../styles/theme';
import SubHeading from '../../components/SubHeading/SubHeading';
import ThreeCards from '../../components/ThreeCards/ThreeCards';
import Content from '../../components/Content/Content';
import ContentSidebar from '../../components/ContentSidebar/ContentSidebar';
import SidebarDetail from '../../components/SidebarDetail/SidebarDetail';
import ContentTwo from '../../components/ContentTwo/ContentTwo';
import ContactUsForm from '../../components/Shared/ContactUsForm';
import Card from '../../components/Card/Card';
import createGTMClass from '../../util/createGTMClass';
import LocationCards from '../../components/LocationCards/LocationCards';
import ArrowIcon from '../../components/Shared/ArrowIcon';

function ContactUs({ isLoading = false, className }) {
  return (
    <Layout isLoading={isLoading} className={className}>
      <SEO
        title='Contact Us'
        description='If you have questions or concerns about the servicing of the loan, feel free to contact us via phone, email or U.S. mail for personal assistance and support.'
      />
      <Heading background={theme.colors.blue10} appearance='dark'>
        <h1>Contact Us</h1>
      </Heading>
      <SubHeading>
        <h2 className='type-h5'>Looking to make a payment?</h2>
        <Link
          to='/payment-options/'
          className={createGTMClass('payment_options', 'hero', 'text')}
        >
          Learn about our payment options <ArrowIcon />
        </Link>
      </SubHeading>
      <ContentSidebar
        sidebarWidth='392px'
        sidebar={
          <SidebarDetail
            title='Give us a call. We’re here for you.'
            description='When you call, please have your loan number available so we can
                         provide you with prompt and quality service.'
            children={
              <>
                <div className='hours'>
                  <h4 className='type-h5'>Hours of Operation:</h4>
                  <p className='type-caption'>
                    <span className='type-h6 color-claytonBlue'>
                      Mon - Thu:
                    </span>{' '}
                    8:30am - 8:00pm ET
                  </p>
                  <p className='type-caption'>
                    <span className='type-h6 color-claytonBlue'>Fri:</span>{' '}
                    8:30am - 5:30pm ET
                  </p>
                </div>
                <ContentTwo
                  justifyContent={false}
                  padding={false}
                  columnOne={<h4 className='type-h5'>General Questions</h4>}
                  columnTwo={
                    <p className='type-intro'>
                      <a
                        href='tel:18009707250'
                        className={createGTMClass('phone', 'sidebargq', 'text')}
                      >
                        1-800-970-7250
                      </a>
                    </p>
                  }
                />
                <ContentTwo
                  justifyContent={false}
                  padding={false}
                  columnOne={
                    <>
                      <h4 className='type-h5'>Existing Customers</h4>
                      <p className='type-caption'>
                        For questions about an existing home loan
                      </p>
                    </>
                  }
                  columnTwo={
                    <p className='type-intro'>
                      <a
                        href='tel:18009707250'
                        className={createGTMClass('phone', 'sidebarec', 'text')}
                      >
                        1-800-970-7250
                      </a>
                    </p>
                  }
                />
                <ContentTwo
                  justifyContent={false}
                  padding={false}
                  columnOne={
                    <>
                      <h4 className='type-h5'>New Customers</h4>
                      <p className='type-caption'>
                        For questions about applying for a home loan
                      </p>
                    </>
                  }
                  columnTwo={
                    <p className='type-intro'>
                      <a
                        href='tel:18667010467'
                        className={createGTMClass('phone', 'sidebarnc', 'text')}
                      >
                        1-866-701-0467
                      </a>
                    </p>
                  }
                />
                <ContentTwo
                  justifyContent={false}
                  padding={false}
                  columnOne={
                    <>
                      <h4 className='type-h5'>FAX Number</h4>
                    </>
                  }
                  columnTwo={
                    <p className='type-intro'>
                      <a
                        href='tel:18667010467'
                        className={createGTMClass('phone', 'sidebarnc', 'text')}
                      >
                        1-865-380-3418
                      </a>
                    </p>
                  }
                />
                <ContentTwo
                  justifyContent={false}
                  padding={false}
                  columnOne={
                    <>
                      <h4 className='type-h5'>
                        Home UpGrade and TVA EnergyRight<sup>®</sup> loans
                      </h4>
                      <p className='type-caption'>
                        For questions about an existing HUG or TVA EnergyRight
                        loan
                      </p>
                    </>
                  }
                  columnTwo={
                    <p className='type-intro'>
                      <a
                        href='tel:18888938792'
                        className={createGTMClass('phone', 'sidebarcl', 'text')}
                      >
                        1-800-970-7250
                      </a>
                    </p>
                  }
                />
                <Content divider={false}>
                  <p className='type-caption'>
                    Any messages left after normal business hours will be
                    returned within 24-48 hours on a normal business day,
                    Monday-Friday.
                  </p>
                </Content>
              </>
            }
          />
        }
      >
        <Card>
          <ContactUsForm />
        </Card>
      </ContentSidebar>
      <ThreeCards
        title='Mail Correspondence'
        children={
          <>
            <p>
              Need to request information or let us know of an error? Want to
              share concerns you have about the servicing of your loan? Reach
              out to us at{' '}
              <a
                href='mailto:customercare@vmf.com'
                className={createGTMClass('email', 'mail', 'text')}
              >
                customercare@vmf.com
              </a>{' '}
              or use one of the addresses below. To help us provide you with a
              quick response, please include your loan number on all
              correspondence.
            </p>
            <p>
              Remember to mail correspondence to us separately from your payment
              and include your loan number with anything you send us. To ensure
              we receive your mail as soon as possible, any correspondence and
              overnight deliveries need to be mailed to our Regular or Overnight
              mailing address below. Overnight services won’t deliver to a post
              office box.
            </p>
          </>
        }
        columnOne={
          <>
            <h3 className='type-h4'>Regular Mail</h3>
            <p>
              Attn: Customer Service
              <br />
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              PO Box 9800
              <br />
              Maryville, TN 37802
            </p>
          </>
        }
        columnTwo={
          <>
            <h3 className='type-h4'>Overnight Mail</h3>
            <p>
              Attn: Customer Service
              <br />
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              500 Alcoa Trail
              <br />
              Maryville, TN 37804
            </p>
          </>
        }
        columnThree={
          <>
            <h3 className='type-h4'>Payoff Loan Payments</h3>
            <p>
              <Link to='/resources/faqs/'>See FAQ page</Link> for instructions
              on obtaining a payoff quote or coupon.
            </p>
          </>
        }
      />
      <LocationCards
        title='Locations'
        cardOne={
          <>
            <h3 className='type-h4'>Tennessee</h3>
            <p className='no-margin-bottom'>
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              500 Alcoa Trail
              <br />
              Maryville, TN 37804
            </p>
            <p className='type-h6'>NMLS #1561</p>
            <p className='small'>
              The Maryville Office is the main office for Vanderbilt Mortgage
              and Finance, Inc.
            </p>
          </>
        }
        cardTwo={
          <>
            <h3 className='type-h4'>Missouri</h3>
            <p className='no-margin-bottom'>
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              3610 Buttonwood Dr, Suite 218
              <br />
              Columbia, MO 65201
              <br />
              No. 19-1868
            </p>
            <p className='type-h6'>NMLS #1227976</p>
            <p className='small'>
              Since the office hours vary at this location, please call{' '}
              <a
                href='tel:1-800-970-7250'
                className={createGTMClass('phone', 'locations', 'text')}
              >
                1-800-970-7250
              </a>{' '}
              for an appointment.
            </p>
            <p className='small'>
              <strong className='space'>
                Please do not mail correspondence to this address.
              </strong>
            </p>
          </>
        }
        cardThree={
          <>
            <h3 className='type-h4'>Arizona</h3>
            <p className='no-margin-bottom'>
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              1734 E. Boston St., Suite 103
              <br />
              Gilbert, AZ 85295
              <br />
              BKBR--0115433
            </p>
            <p className='type-h6'>NMLS #151280</p>
            <p className='small'>
              <strong>
                Please do not mail correspondence to this address.
              </strong>
            </p>
          </>
        }
        cardFour={
          <>
            <h3 className='type-h4'>North Carolina</h3>
            <p className='no-margin-bottom'>
              Vanderbilt Mortgage and Finance, Inc.
              <br />
              7900 Triad Center Drive, Suite 400
              <br />
              Greensboro, NC 27409
              <br />
            </p>
            <p className='type-h6'>NMLS #2561044</p>
            <p className='small'>
              <strong>
                Please do not mail correspondence to this address.
              </strong>
            </p>
          </>
        }
      />
    </Layout>
  );
}

ContactUs.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default ContactUsStyles(ContactUs);
